import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_BOARD_INSTANCE_GROUP_TYPE } from 'constants/general';
import {
  useDeleteValueChainMapUserFiltersMutation,
  useGetValueChainMapUserFiltersQuery,
  useSaveValueChainMapUserFiltersMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import buildFilterCatalogue from 'utils/buildFilterCatalogue';
import {
  DEFAULT_FOLLOW_CONNECTIONS,
  fromSavedPayload,
  LIST_PAYLOAD_KEYS,
  serializeSavedPayload,
  toSavedPayload,
} from 'utils/valueChainUserFilters';

import type IOrgStructureItem from 'store/slices/dataSets/interfaces/IOrgStructureItem';
import type { IBoardFilter } from 'store/slices/valueChainMap/interfaces/IBoardFilter';
import type { TBoardInstanceGroupType } from 'store/slices/valueChainMap/interfaces/IBoardInstanceGroup';
import type { IUserFiltersPayload } from 'store/slices/valueChainMap/interfaces/IUserFilters';
import type { TCountryDetailsLookup } from 'utils/country';
import type IFilterData from '../interfaces/IFilterData';

export interface IUseUserFiltersParams {
  valueChainMapId?: string,
  rawCatalogue?: IBoardFilter,
  countryDetailsLookup: TCountryDetailsLookup,
  orgStructureLeaves: IOrgStructureItem[],
  selectedCurrentFilters: IFilterData[],
  followConnections: boolean,
  groupByType: TBoardInstanceGroupType,
  // "Settled" means resolved OR failed — never "has data". A failed supporting
  // query must still release the gate, or the board would never load at all.
  areDependenciesSettled: boolean,
  setSelectedCurrentFilters: (filters: IFilterData[]) => void,
  setFollowConnections: (value: boolean) => void,
  setGroupByType: (value: TBoardInstanceGroupType) => void,
  // Rebuilds the board query string. Called in the same batch that opens the
  // gate, so the first board request already carries the restored filters.
  applyFilters: (filters: IFilterData[]) => void,
}

export interface IUseUserFiltersResult {
  // True until saved filters have been applied (or shown to be absent). The board
  // query waits on this so a map opens with one request instead of two.
  isRestorePending: boolean,
}

const PERSIST_DELAY_MS = 500;

const isEmptyPayload = (payload: IUserFiltersPayload): boolean => (
  LIST_PAYLOAD_KEYS.every((key) => !payload[key] || payload[key]?.length === 0)
  && !payload.revenue
  && (payload.followConnections ?? DEFAULT_FOLLOW_CONNECTIONS) === DEFAULT_FOLLOW_CONNECTIONS
  && (payload.groupByType ?? DEFAULT_BOARD_INSTANCE_GROUP_TYPE) === DEFAULT_BOARD_INSTANCE_GROUP_TYPE
);

// The state a map holds before anything is restored into it. A write of exactly
// this must never leave the browser: it carries no user intent.
// followConnections stays on. Only an explicit saved `false` turns it off.
const MOUNT_STATE_PAYLOAD = serializeSavedPayload(toSavedPayload({
  selectedFilters: [],
  followConnections: DEFAULT_FOLLOW_CONNECTIONS,
  groupByType: DEFAULT_BOARD_INSTANCE_GROUP_TYPE,
}));

// Persists the current user's filters per map and restores them on open.
// Two guards keep a write from destroying a good saved row: restoredMapId blocks
// every write until the restore has run, and persistedPayloadRef blocks the one
// write the restore itself would otherwise trigger.
const useUserFilters = ({
  valueChainMapId,
  rawCatalogue,
  countryDetailsLookup,
  orgStructureLeaves,
  selectedCurrentFilters,
  followConnections,
  groupByType,
  areDependenciesSettled,
  setSelectedCurrentFilters,
  setFollowConnections,
  setGroupByType,
  applyFilters,
}: IUseUserFiltersParams): IUseUserFiltersResult => {
  const { t } = useTranslation();
  // Holds the map id that has finished restoring. Writes stay blocked until it
  // matches. State rather than a ref: the board query gate has to re-render on it.
  const [restoredMapId, setRestoredMapId] = useState<string | undefined>(undefined);
  // Serialized payload that the screen already reflects. The restore sets it
  // before it opens the write gate, so the restore never writes its own output
  // back. Without it a failed GET (nothing restored) or an unbuildable catalogue
  // (filters dropped) would overwrite a good saved row seconds after open.
  const persistedPayloadRef = useRef<string | undefined>(undefined);
  // Last payload applied from the server. A later cache update may re-apply only
  // while the screen still matches this value, so a local edit is never replaced.
  const serverAppliedRef = useRef<string | undefined>(undefined);
  // Serialized server row already copied onto the screen. The same row must not
  // be applied again, or a user edit in this render would be overwritten.
  const appliedServerKeyRef = useRef<string | undefined>(undefined);
  // The restore and the write effect run in one commit. Skip that write: its
  // props still show the screen from before the restore.
  const skipNextWriteRef = useRef(false);
  const pendingPayloadRef = useRef<IUserFiltersPayload | undefined>(undefined);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data: savedRow, isSuccess, isError } = useGetValueChainMapUserFiltersQuery(
    { valueChainMapId: valueChainMapId || '' },
    { skip: !valueChainMapId },
  );
  const [saveUserFilters] = useSaveValueChainMapUserFiltersMutation();
  const [deleteUserFilters] = useDeleteValueChainMapUserFiltersMutation();

  useEffect(() => {
    if (restoredMapId && restoredMapId !== valueChainMapId) {
      persistedPayloadRef.current = undefined;
      serverAppliedRef.current = undefined;
      appliedServerKeyRef.current = undefined;
      setRestoredMapId(undefined);
    }
  }, [valueChainMapId, restoredMapId]);

  useEffect(() => {
    if (!valueChainMapId) {
      return;
    }
    if (!isSuccess && !isError) {
      return;
    }

    const payload = !isError ? savedRow?.data?.filters : undefined;
    const incomingServerKey = payload ? serializeSavedPayload(payload) : MOUNT_STATE_PAYLOAD;
    // This server snapshot is already on the screen. Leave local edits alone.
    if (restoredMapId === valueChainMapId && appliedServerKeyRef.current === incomingServerKey) {
      return;
    }
    const screenMatchesServer = !persistedPayloadRef.current
      || persistedPayloadRef.current === serverAppliedRef.current;
    // A local edit owns the screen. A late cache update must not replace it.
    if (restoredMapId === valueChainMapId && !screenMatchesServer) {
      return;
    }

    // A 404, a failed request or an empty row all mean "nothing saved".
    if (isError || !payload) {
      // Nothing to restore, so put the screen back to the mount state. Without
      // this the previous map's filters stay applied when this map has no row.
      // After a failed GET the saved row may well be intact. The write gate holds
      // the same mount state as its baseline, so this never deletes it.
      if (
        restoredMapId === valueChainMapId
        && persistedPayloadRef.current === MOUNT_STATE_PAYLOAD
        && serverAppliedRef.current === MOUNT_STATE_PAYLOAD
      ) {
        return;
      }
      persistedPayloadRef.current = MOUNT_STATE_PAYLOAD;
      serverAppliedRef.current = MOUNT_STATE_PAYLOAD;
      appliedServerKeyRef.current = incomingServerKey;
      // The first apply lands before restoredMapId updates, so the write effect
      // already bails. A later apply shares the commit with a live write effect.
      if (restoredMapId === valueChainMapId) {
        skipNextWriteRef.current = true;
      }
      setSelectedCurrentFilters([]);
      setFollowConnections(DEFAULT_FOLLOW_CONNECTIONS);
      setGroupByType(DEFAULT_BOARD_INSTANCE_GROUP_TYPE);
      applyFilters([]);
      setRestoredMapId(valueChainMapId);
      return;
    }
    if (!areDependenciesSettled) {
      return;
    }

    const restored = fromSavedPayload({
      payload,
      catalogue: buildFilterCatalogue({
        rawFilters: rawCatalogue,
        countryDetailsLookup,
        translate: (key: string, fallback: string) => t(key, fallback),
      }),
      orgStructureLeaves,
    });

    // What the screen is about to hold. Recording it here stops the write gate
    // from echoing the restore back — which matters most when the catalogue was
    // unavailable and `restored.filters` is a pruned copy of the saved row.
    const serialized = serializeSavedPayload(toSavedPayload({
      selectedFilters: restored.filters,
      followConnections: restored.followConnections,
      groupByType: restored.groupByType,
    }));
    if (
      restoredMapId === valueChainMapId
      && persistedPayloadRef.current === serialized
      && serverAppliedRef.current === serialized
    ) {
      return;
    }
    persistedPayloadRef.current = serialized;
    serverAppliedRef.current = serialized;
    appliedServerKeyRef.current = incomingServerKey;
    if (restoredMapId === valueChainMapId) {
      skipNextWriteRef.current = true;
    }
    setSelectedCurrentFilters(restored.filters);
    setFollowConnections(restored.followConnections);
    setGroupByType(restored.groupByType);
    // Same batch as the gate opening, so the board's first request is filtered.
    // Leaving this to the effect that watches selectedCurrentFilters would let
    // one unfiltered request out while the query string caught up.
    applyFilters(restored.filters);
    setRestoredMapId(valueChainMapId);
  }, [
    valueChainMapId, restoredMapId, isSuccess, isError, savedRow, rawCatalogue,
    areDependenciesSettled, countryDetailsLookup, orgStructureLeaves, t,
    setSelectedCurrentFilters, setFollowConnections, setGroupByType, applyFilters,
  ]);

  const persist = useCallback((payload: IUserFiltersPayload) => {
    if (!valueChainMapId) {
      return;
    }

    const request = isEmptyPayload(payload)
      ? deleteUserFilters({ valueChainMapId })
      : saveUserFilters({ valueChainMapId, filters: payload });

    request.unwrap().catch((error: unknown) => {
      // Persistence is a background convenience: log, never interrupt the board.
      // eslint-disable-next-line no-console
      console.error('[ATT-113] failed to persist value chain user filters', error);
    });
  }, [valueChainMapId, saveUserFilters, deleteUserFilters]);

  const persistRef = useRef(persist);
  persistRef.current = persist;

  const schedulePersist = useCallback((payload: IUserFiltersPayload) => {
    pendingPayloadRef.current = payload;
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = setTimeout(() => {
      const nextPayload = pendingPayloadRef.current;
      pendingPayloadRef.current = undefined;
      persistTimerRef.current = undefined;
      if (nextPayload) {
        persistRef.current(nextPayload);
      }
    }, PERSIST_DELAY_MS);
  }, []);

  // A navigation inside the debounce window must still write. The timer dies
  // with the component, so flush the pending payload on unmount.
  useEffect(() => () => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = undefined;
    }
    const nextPayload = pendingPayloadRef.current;
    pendingPayloadRef.current = undefined;
    if (nextPayload) {
      persistRef.current(nextPayload);
    }
  }, []);

  useEffect(() => {
    // No baseline means the restore has not run for this map, so nothing on the
    // screen can be trusted to describe the user's intent yet.
    if (!valueChainMapId || restoredMapId !== valueChainMapId || !persistedPayloadRef.current) {
      return;
    }
    if (skipNextWriteRef.current) {
      skipNextWriteRef.current = false;
      return;
    }

    const payload = toSavedPayload({
      selectedFilters: selectedCurrentFilters,
      followConnections,
      groupByType,
    });
    const serializedPayload = serializeSavedPayload(payload);

    if (serializedPayload === persistedPayloadRef.current) {
      return;
    }

    persistedPayloadRef.current = serializedPayload;
    schedulePersist(payload);
  }, [
    valueChainMapId, restoredMapId, selectedCurrentFilters, followConnections,
    groupByType, schedulePersist,
  ]);

  return { isRestorePending: Boolean(valueChainMapId) && restoredMapId !== valueChainMapId };
};

export default useUserFilters;
